import { Anchor, Button, Group, Menu, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import {
  isCancelled,
  formatDuration,
  holdsPlace,
  isExternal,
  minutesToHm,
  publicationState,
  type AutofillPlan,
  type AutofillProposal,
  type MinuteRange,
  type ShiftPlanning,
} from '@reditelji/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCheckinMutations, useShiftMutations } from '../api/hooks';
import { useSession } from '../api/session';
import { recordedOn } from '../volunteers/cancelCopy';
import { answeredRefusals, confirmedShifts, standingWords, unansweredShifts } from '../volunteers/derive';
import { ANSWER_CHIP } from '../volunteers/ui';
import { useDeepLinks } from '../lib/deepLinks';
import { plural } from '../lib/ui';
import type { PoolVolunteer, Shift, TimelinePayload, TimelinePost } from '../api/types';
import { checkinActions, checkinsByShift, ciState, liveVals, liveViewsByShift, seenIn } from '../live/derive';
import type { DayPhase } from '../lib/ui';
import { useUi, type AsideTab } from '../state/ui';
import { LivePanel } from './LivePanel';
import { PlanPanel } from './PlanPanel';
import type { PlanHeadline, ReviewSplit } from './planReview';
import {
  awaitsConfirmation,
  availabilityBucket,
  BUCKET_ORDER,
  briefingStale,
  cancelledMatching,
  dayStatusKey,
  gapMismatch,
  holderLabel,
  initials,
  mismatchedShifts,
  pieceCandidates,
  planningOn,
  range,
  reasonChips,
  refusedShiftsOnDay,
  rosterPool,
  shiftConfirmation,
  slotLabel,
  trimFor,
  type LensFit,
} from './derive';
import { useAxis } from './axisContext';
import { Load } from './candidateUi';
import { DayStripLegend } from './DayStrip';
import { LensPanel } from './LensPanel';
import { DraggableVolunteerCard, VolunteerCard } from './PoolCard';
import { FillDetail } from './FillDetail';
import { useCoordinatorLabel } from '../lib/people';
import { PersonPicker } from './PersonPicker';
import { ShiftAnswerBox } from './ShiftAnswerBox';
import { PostLink, shiftWindow, timeOptions } from './shiftForm';
import { useSchedule } from './scheduleContext';

export interface SideLens {
  person: PoolVolunteer;
  fits: LensFit[];
  canAct: boolean;
  onAssign: (fit: LensFit) => void;
}

export function SidePanel({
  data,
  plan,
  review,
  headline,
  phase,
  live,
  now,
  planning,
  silent,
  onAssignPiece,
  onAcceptClean,
  onAcceptMany,
  onRejectAll,
  onAcceptProposal,
  onRejectProposal,
  lens,
}: {
  data: TimelinePayload;
  plan: AutofillPlan | null;
  review: ReviewSplit | null;
  headline: PlanHeadline | null;
  phase: DayPhase;
  live: boolean;
  now: number;
  planning: ShiftPlanning;
  silent: boolean;
  onAssignPiece: (volunteer: PoolVolunteer, piece: MinuteRange & { postId: string }, narrows: boolean) => void;
  onAcceptClean: () => void;
  onAcceptMany: (picked: AutofillProposal[]) => void;
  onRejectAll: () => void;
  onAcceptProposal: (p: AutofillProposal) => void;
  onRejectProposal: (p: AutofillProposal) => void;
  /** The person the board looks through, when that applies (SPEC §1.1B). */
  lens: SideLens | null;
}) {
  const { asideTab, setAsideTab } = useUi();
  const warnings = useWarnings(data, live, phase);
  const freeCount = rosterPool(data).filter((v) => v.dayMinutes === 0).length;
  const lateCount = live ? liveVals(data, now, true).lateList.length : 0;

  // Podrobnosti leads — it is the tab a coordinator works in. Then the two
  // conditional ones (a plan under review, live mode), the pool, and Opozorila
  // last as the day's to-do list. Selecting anything already switches tab, so
  // nothing loses focus by moving.
  const tabs: { key: AsideTab; label: string; badge?: number; blue?: boolean }[] = [
    { key: 'podrobnosti', label: 'Podrobnosti' },
    ...(plan && review
      ? [{ key: 'predlog' as const, label: 'Predlog', badge: review.exceptions.length, blue: true }]
      : []),
    ...(live ? [{ key: 'zivo' as const, label: 'V živo', badge: lateCount }] : []),
    ...(phase !== 'past'
      ? [{ key: 'prostovoljci' as const, label: 'Prostovoljci', badge: freeCount, blue: true }]
      : []),
    { key: 'opozorila', label: 'Opozorila', badge: warnings.total },
  ];
  const tab = tabs.some((t) => t.key === asideTab) ? asideTab : live ? 'zivo' : 'opozorila';

  return (
    <aside className="tl-aside">
      <div className="tl-aside-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tl-aside-tab${tab === t.key ? ' tl-aside-tab--active' : ''}`}
            onClick={() => setAsideTab(t.key)}
          >
            {t.label}
            {t.badge ? (
              <span className={`tl-aside-badge${t.blue ? ' tl-aside-badge--blue' : ''}`}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="tl-aside-body">
        {tab === 'predlog' && plan && review && headline ? (
          <PlanPanel
            review={review}
            headline={headline}
            unfilled={plan.unfilled}
            onAcceptClean={onAcceptClean}
            onAcceptMany={onAcceptMany}
            onRejectAll={onRejectAll}
            onAcceptProposal={onAcceptProposal}
            onRejectProposal={onRejectProposal}
          />
        ) : null}
        {tab === 'zivo' ? <LivePanel data={data} now={now} /> : null}
        {tab === 'opozorila' ? <WarningsPanel warnings={warnings} /> : null}
        {tab === 'prostovoljci' ? (
          lens ? (
            <LensPanel
              data={data}
              person={lens.person}
              fits={lens.fits}
              planning={planning}
              silent={silent}
              canAct={lens.canAct}
              onAssign={lens.onAssign}
            />
          ) : (
            <PoolPanel data={data} />
          )
        ) : null}
        {tab === 'podrobnosti' ? (
          <DetailPanel data={data} phase={phase} live={live} now={now} planning={planning} silent={silent} onAssignPiece={onAssignPiece} />
        ) : null}
      </div>
    </aside>
  );
}

// ---------- Opozorila ----------

interface WarningItem {
  key: string;
  title: string;
  detail: string;
  onClick?: () => void;
}
interface WarningGroup {
  label: string;
  items: WarningItem[];
}

function useWarnings(data: TimelinePayload, live: boolean, phase: DayPhase) {
  const { selectShift, selectGap } = useUi();
  const navigate = useNavigate();
  return useMemo(() => {
    const volunteersById = new Map(data.volunteers.map((v) => [v.id, v]));
    const groups: WarningGroup[] = [];

    // Per shift, on the day of that shift (SPEC §1.1F): a person's status reads
    // "zavrnjen" for a refusal on any day, and would list them on every day
    // they work. Every login is told where shifts stand, so the list is the
    // same for all; the words are there only for a login shown them.
    const refused = refusedShiftsOnDay(data);
    const item = (r: (typeof refused.cant)[number], fallback: string) => ({
      key: `ref-${r.shift.id}`,
      title: `${r.volunteer.name} — ${r.post.code} ${range(r.shift.fromMin, r.shift.toMin)}`,
      detail: r.comment ? `„${r.comment}“` : fallback,
      onClick: () => selectShift(r.shift.id),
    });
    if (refused.cant.length) {
      groups.push({ label: 'Zavrnjene izmene', items: refused.cant.map((r) => item(r, 'ne more · brez pojasnila')) });
    }
    if (refused.change.length) {
      groups.push({ label: 'Predlogi sprememb', items: refused.change.map((r) => item(r, 'predlaga spremembo')) });
    }
    if (refused.whole.length) {
      groups.push({
        label: 'Zavrnjeni razporedi',
        items: refused.whole.map((v) => ({
          key: `rej-${v.id}`,
          title: `${v.name} — razpored zavrnjen`,
          // The words are theirs to read only with comms:read (SPEC §1.1L).
          detail: v.confirmationComment === undefined ? 'odgovor pri ekipi redarjev' : v.confirmationComment || 'brez pojasnila',
          onClick: () => {
            const s = data.posts.flatMap((p) => p.shifts).find((x) => x.volunteerId === v.id && x.state === 'assigned');
            if (s) selectShift(s.id);
          },
        })),
      });
    }

    const gapItems: WarningItem[] = data.posts.flatMap((p) => [
      ...p.gaps.map((g) => ({
        key: `gap-${p.id}-${g.fromMin}`,
        title: `Nepokrito ${range(g.fromMin, g.toMin)}`,
        detail: `${p.code} · ${p.micro} · ${formatDuration(g.toMin - g.fromMin)} brez redarja`,
        onClick: () => selectGap({ postId: p.id, fromMin: g.fromMin, toMin: g.toMin }),
      })),
      ...p.shifts
        .filter((s) => s.state === 'open')
        .map((s, i) => ({
          key: `open-${s.id}`,
          title: `Izmena ${i + 1} brez osebe`,
          detail: `${p.code} · ${range(s.fromMin, s.toMin)}`,
          onClick: () =>
            selectGap({ postId: p.id, fromMin: s.fromMin, toMin: s.toMin, shiftId: s.id }),
        })),
    ]);
    if (gapItems.length) groups.push({ label: 'Nepokrita mesta', items: gapItems });

    const dayConflicts = data.conflicts.filter((c) => c.eventDayId === data.day.id);
    const overlapItems = dayConflicts
      .filter((c) => c.kind === 'overlap')
      .map((c) => ({
        key: `ovl-${c.shiftIds[0]}-${c.shiftIds[1]}`,
        title: `${volunteersById.get(c.volunteerId)?.name ?? '?'} na dveh mestih`,
        detail: 'prekrivanje izmen',
        onClick: () => selectShift(c.shiftIds[1]),
      }));
    if (overlapItems.length) groups.push({ label: 'Dvojne zasedbe', items: overlapItems });

    // The same fact as the pool's flagged group, but after the fact: what came
    // in by drag, from an earlier session, or because somebody changed their
    // sheet *after* being scheduled. Planning only, exactly like the board's ▲
    // marks it points at: on the day what somebody wrote on a signup sheet a
    // month earlier changes nothing, and a warning about a marshal already
    // standing at the post would have no mark to select.
    const mismatched = planningOn(live, phase)
      ? mismatchedShifts(data).map(({ post: p, shift: sh, volunteer: v, closed }) => ({
          key: `avail-${sh.id}`,
          title: `${v.name} — ${p.code}`,
          detail: `${closed.map(slotLabel).join(', ')} · ${range(sh.fromMin, sh.toMin)}`,
          onClick: () => selectShift(sh.id),
        }))
      : [];
    if (mismatched.length)
      groups.push({ label: 'Razporejeni, ko so javili, da ne morejo', items: mismatched });

    const gapConflicts = dayConflicts
      .filter((c) => c.kind === 'short-gap')
      .map((c) => ({
        key: `sg-${c.shiftIds[0]}-${c.shiftIds[1]}`,
        title: `${volunteersById.get(c.volunteerId)?.name ?? '?'} — razmik ${c.gapMin} min`,
        detail: 'manj kot 1 h med izmenama',
        onClick: () => selectShift(c.shiftIds[1]),
      }));
    if (gapConflicts.length)
      groups.push({ label: 'Premajhen razmik med izmenami', items: gapConflicts });

    const urgentWaiting = data.posts
      .filter((p) => p.urgent)
      .flatMap((p) =>
        p.shifts
          .filter((s) => {
            const v = s.volunteerId ? volunteersById.get(s.volunteerId) : undefined;
            return awaitsConfirmation(s, v);
          })
          .map((s) => ({
            key: `urg-${s.id}`,
            title: 'Nujno mesto ni potrjeno',
            detail: `${p.code} · ${volunteersById.get(s.volunteerId!)?.name ?? ''} · ${range(s.fromMin, s.toMin)}`,
            onClick: () => selectShift(s.id),
          })),
      );
    if (urgentWaiting.length)
      groups.push({ label: 'Nujna mesta brez potrditve', items: urgentWaiting });

    const longDayItems = data.longDays
      .filter((l) => l.eventDayId === data.day.id)
      .map((l) => ({
        key: `long-${l.volunteerId}`,
        title: `${volunteersById.get(l.volunteerId)?.name ?? '?'} — dolg dan`,
        detail: `${formatDuration(l.totalMin)} razporejenih (meja 10 h)`,
      }));
    if (longDayItems.length) groups.push({ label: 'Predolge izmene', items: longDayItems });

    const staleBriefing = data.volunteers.filter(
      (v) =>
        briefingStale(v) &&
        data.posts.some((p) => p.shifts.some((s) => s.volunteerId === v.id && s.state !== 'done')),
    );
    if (staleBriefing.length) {
      groups.push({
        label: 'Manjka brifing',
        items: staleBriefing.map((v) => ({
          key: `brief-${v.id}`,
          title: 'Brifing ni poslan',
          detail: v.name,
          // The board's own view has no answer for this one — sending is done
          // on the person's page, so that is where the item points.
          onClick: () => void navigate(`/prostovoljci?volunteer=${v.id}`),
        })),
      });
    }

    /*
     * Their page shows an older schedule than this board does (SPEC §1.1F).
     * A different question from "Manjka brifing", which is about whether we
     * ever wrote to them: somebody briefed by phone is mailed-never and
     * published-current, and somebody whose shift moved this morning is the
     * other way round. In "Po mestih" nothing else says this, since the amber
     * row tint only exists in "Po ljudeh".
     */
    const unpublished = rosterPool(data).filter(
      (v) =>
        // Scoped to this day like every other group here: a person whose
        // Friday is unpublished has no business nagging from Sunday's board.
        data.posts.some((p) =>
          p.shifts.some((s) => s.volunteerId === v.id && s.state !== 'done'),
        ) &&
        // Not told what was published without `comms:read` (SPEC §1.1L).
        v.briefingSentVersion !== undefined &&
        publicationState({
          scheduleVersion: v.scheduleVersion,
          briefingSentVersion: v.briefingSentVersion,
          hasShifts: true,
        }) === 'neobjavljeno',
    );
    if (unpublished.length) {
      groups.push({
        label: 'Neobjavljene spremembe',
        items: unpublished.map((v) => ({
          key: `unpub-${v.id}`,
          title: v.name,
          detail: 'vidi starejši razpored — objavi ali povrni',
          onClick: () => void navigate(`/prostovoljci?volunteer=${v.id}`),
        })),
      });
    }

    return { groups, total: groups.reduce((a, g) => a + g.items.length, 0) };
  }, [data, live, phase, selectShift, selectGap, navigate]);
}

function WarningsPanel({ warnings }: { warnings: { groups: WarningGroup[]; total: number } }) {
  if (warnings.total === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Brez odprtih opozoril za ta dan.
      </Text>
    );
  }
  return (
    <Stack gap={4}>
      {warnings.groups.map((g) => (
        <div key={g.label}>
          <div className="tl-pool-group">
            <span className="tl-pool-group-dot" style={{ background: 'oklch(0.6 0.12 25)' }} />
            {g.label}
          </div>
          {g.items.map((item) => (
            <div
              key={item.key}
              className="tl-pool-card"
              style={{ cursor: item.onClick ? 'pointer' : 'default' }}
              onClick={item.onClick}
            >
              <Text size="sm" fw={600}>
                {item.title}
              </Text>
              <Text size="xs" c="dimmed">
                {item.detail}
              </Text>
            </div>
          ))}
        </div>
      ))}
    </Stack>
  );
}

// ---------- Prostovoljci (the pool, to browse and drag from) ----------

function PoolPanel({ data }: { data: TimelinePayload }) {
  const { poolSearch, setPoolSearch } = useUi();
  const search = poolSearch.trim().toLowerCase();
  const matches = (v: PoolVolunteer) => !search || v.name.toLowerCase().includes(search);
  return (
    <Stack gap={8}>
      <Text size="xs" c="dimmed">
        Povleci osebo na mesto. Za razvrščen seznam klikni rdeče polje{' '}
        <Text component="span" size="xs" style={{ fontFamily: 'var(--rr-font-mono)', color: 'oklch(0.5 0.12 25)' }}>
          PROSTO
        </Text>
        .
      </Text>
      <DayStripLegend />
      <TextInput
        size="xs"
        placeholder="Išči po imenu ali oznaki…"
        value={poolSearch}
        onChange={(e) => setPoolSearch(e.currentTarget.value)}
      />
      <BrowsePool data={data} matches={matches} query={poolSearch} />
    </Stack>
  );
}

function BrowsePool({
  data,
  matches,
  query,
}: {
  data: TimelinePayload;
  matches: (v: PoolVolunteer) => boolean;
  query: string;
}) {
  // Teams are never in the pool: they are assigned deliberately, not browsed.
  const filtered = rosterPool(data).filter(matches);
  const buckets = BUCKET_ORDER.map((b) => ({
    label: b,
    list: filtered.filter((v) => availabilityBucket(v.availability, data.day.key) === b),
  }));
  if (filtered.length === 0 && cancelledMatching(data, query).length === 0) {
    return (
      <Text size="xs" c="dimmed">
        Ni zadetkov.
      </Text>
    );
  }
  return (
    <>
      {buckets.map((b) =>
        b.list.length > 0 ? (
          <div key={b.label}>
            <div className="tl-pool-group">
              <span className="tl-pool-group-dot" style={{ background: 'oklch(0.62 0.07 245)' }} />
              {b.label}
            </div>
            {b.list.map((v) => (
              <DraggableVolunteerCard key={v.id} volunteer={v} data={data} dot={dayStatusKey(v, data)} />
            ))}
          </div>
        ) : null,
      )}
      <CancelledGroup data={data} query={query} />
    </>
  );
}

/**
 * People who are not coming whose name the search matches (SPEC §1.1K).
 * Never offered — neither Dodeli nor a drag handle — but listed, so somebody
 * looking for a name learns why it is missing, and reaches the card.
 */
function CancelledGroup({ data, query }: { data: TimelinePayload; query: string }) {
  const navigate = useNavigate();
  const list = cancelledMatching(data, query);
  if (list.length === 0) return null;
  return (
    <div>
      <div className="tl-pool-group">
        <span className="tl-pool-group-dot" style={{ background: 'oklch(0.55 0.14 25)' }} />
        Ne pridejo
      </div>
      {list.map((v) => (
        <VolunteerCard
          key={v.id}
          volunteer={v}
          out
          note={`ne pride · zabeleženo ${recordedOn(v.cancelledAt)}`}
          action={
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => void navigate(`/prostovoljci?volunteer=${v.id}`)}
            >
              Profil
            </Button>
          }
        />
      ))}
    </div>
  );
}

// ---------- Podrobnosti ----------

type AssignPiece = (volunteer: PoolVolunteer, piece: MinuteRange & { postId: string }, narrows: boolean) => void;

function DetailPanel({
  data,
  phase,
  live,
  now,
  planning,
  silent,
  onAssignPiece,
}: {
  data: TimelinePayload;
  phase: DayPhase;
  live: boolean;
  now: number;
  planning: ShiftPlanning;
  silent: boolean;
  onAssignPiece: AssignPiece;
}) {
  const { selectedShiftId, selectedGap } = useUi();
  const found = useMemo(() => {
    for (const p of data.posts) {
      const s = p.shifts.find((x) => x.id === selectedShiftId);
      if (s) return { post: p, shift: s };
    }
    return null;
  }, [data, selectedShiftId]);

  // An uncovered stretch — a gap, or an open row however it was selected — is
  // about who could stand there.
  const openRow =
    found && found.shift.state === 'open' && found.shift.volunteerId === null
      ? { postId: found.post.id, fromMin: found.shift.fromMin, toMin: found.shift.toMin, shiftId: found.shift.id }
      : null;
  const gap = selectedGap ?? openRow;
  if (gap) {
    return (
      <FillDetail data={data} sel={gap} phase={phase} planning={planning} silent={silent} onAssign={onAssignPiece} />
    );
  }
  if (!found) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Izberi izmeno ali rdeče polje PROSTO na časovnici.
        {phase !== 'past' ? ' N — naslednja vrzel.' : ''}
      </Text>
    );
  }
  return (
    <ShiftDetail
      key={found.shift.id + found.shift.updatedAt}
      {...found}
      data={data}
      phase={phase}
      live={live}
      now={now}
      planning={planning}
    />
  );
}

/**
 * Check-in card in the detail panel: shows on any
 * non-future day for a staffed shift, with org check-in/out and undo.
 */
function LiveCheckinCard({
  shift,
  data,
  phase,
  live,
  now,
}: {
  shift: Shift;
  data: TimelinePayload;
  phase: DayPhase;
  live: boolean;
  now: number;
}) {
  const checkinMut = useCheckinMutations();
  // Watching race day shows who is on site; marking it is the marshal team's (SPEC §1.1L).
  const canAct = useSession().can('live:act');
  const labelOf = useCoordinatorLabel();
  const cm = useMemo(() => checkinsByShift(data.checkins), [data.checkins]);
  // In live mode the card reads what the block it opens from reads: back-to-back
  // shifts as one stretch at the board's minute (`liveShiftViews`). Outside it,
  // the shift's own record.
  const view = useMemo(
    () =>
      (live ? liveViewsByShift(data.posts, cm, now).get(shift.id) : undefined) ?? {
        ...seenIn(cm)(shift),
        lateFrom: shift.fromMin,
        continued: false,
      },
    [live, data.posts, cm, now, shift],
  );
  const ci = cm.get(shift.id);
  if (phase === 'next' || !holdsPlace(shift)) return null;

  const status = ciState(shift, cm, view);
  const holder = data.volunteers.find((v) => v.id === shift.volunteerId);
  // A day that is over is corrected at its end.
  const may = checkinActions(shift, view, !live && phase === 'past' ? 1440 : now, holder !== undefined && isExternal(holder));
  const tone =
    status === 'opravljeno'
      ? { label: 'Opravljeno in zaklenjeno', bg: 'oklch(0.965 0.012 155)', bd: 'oklch(0.89 0.02 155)', fg: 'oklch(0.42 0.03 155)' }
      : status === 'prijavljen'
        ? { label: 'Na mestu', bg: 'oklch(0.95 0.04 230)', bd: 'oklch(0.82 0.07 230)', fg: 'oklch(0.35 0.08 230)' }
        : { label: 'Ni prijavljen', bg: 'oklch(0.975 0.005 95)', bd: 'oklch(0.92 0.006 95)', fg: 'oklch(0.45 0.012 90)' };
  const detail = ci?.in
    ? `prijava ${minutesToHm(ci.in.atMin)} · ${ci.in.by === 'self' ? 'sam prek brifinga' : `označil ${labelOf(ci.in.actorId)}`}${
        view.outAt !== null ? ` · odjava ${minutesToHm(view.outAt)}` : ''
      }`
    : view.inAt !== null
      ? `na mestu od ${minutesToHm(view.inAt)} · prijava na izmeni pred to${
          view.outAt !== null ? ` · odjava ${minutesToHm(view.outAt)}` : ''
        }`
      : view.outAt !== null
        ? `brez prijave · odjava ${minutesToHm(view.outAt)}`
        : 'brez prijave';

  return (
    <div
      style={{
        padding: '10px 11px',
        borderRadius: 9,
        background: tone.bg,
        border: `1px solid ${tone.bd}`,
      }}
    >
      <Group gap={8} align="baseline" wrap="nowrap">
        <Text size="sm" fw={600} style={{ color: tone.fg }}>
          {tone.label}
        </Text>
        <Text
          size="xs"
          style={{ fontFamily: 'var(--rr-font-mono)', color: tone.fg, opacity: 0.75, flex: 1, textAlign: 'right' }}
        >
          {detail}
        </Text>
      </Group>
      {canAct ? (
        <Group gap={5} mt={8} wrap="nowrap">
          {may.in ? (
            <Button
              size="compact-sm"
              variant="default"
              style={{ flex: 1, color: 'oklch(0.35 0.08 230)', borderColor: 'oklch(0.82 0.07 230)' }}
              loading={checkinMut.set.isPending}
              onClick={() => checkinMut.set.mutate({ shiftId: shift.id, kind: 'in' })}
            >
              Označi prijavo
            </Button>
          ) : null}
          {may.out ? (
            <Button
              size="compact-sm"
              variant="default"
              style={{ flex: 1, color: 'oklch(0.35 0.05 155)', borderColor: 'oklch(0.84 0.05 155)' }}
              loading={checkinMut.set.isPending}
              onClick={() => checkinMut.set.mutate({ shiftId: shift.id, kind: 'out' })}
            >
              Označi odjavo
            </Button>
          ) : null}
          {ci?.in || ci?.out ? (
            <Button
              size="compact-sm"
              variant="default"
              loading={checkinMut.clear.isPending}
              onClick={() => {
                if (ci.out) checkinMut.clear.mutate({ shiftId: shift.id, kind: 'out' });
                if (ci.in) checkinMut.clear.mutate({ shiftId: shift.id, kind: 'in' });
              }}
            >
              Razveljavi
            </Button>
          ) : null}
        </Group>
      ) : null}
    </div>
  );
}

/**
 * Everything the person holds, on every day, with what they answered for each
 * (SPEC §1.1F) — so a refusal is read where it was given, not on whichever of
 * their shifts happens to be selected. Summarised in one line; confirming the
 * whole schedule is on their profile.
 */
function PersonShifts({
  volunteer,
  shiftId,
  dayId,
}: {
  volunteer: PoolVolunteer;
  shiftId: string;
  dayId: string;
}) {
  const rollup = useSchedule().get(volunteer.id);
  const { goShift } = useDeepLinks();
  const { selectShift } = useUi();
  const navigate = useNavigate();
  // Where each shift stands — every login is told, so the chips are the same for
  // all. Somebody not coming is asked nothing (SPEC §1.1K): no chip, no count.
  const answers = isCancelled(volunteer) ? undefined : standingWords(volunteer);
  const profile = (
    <Anchor component="button" size="xs" onClick={() => void navigate(`/prostovoljci?volunteer=${volunteer.id}`)}>
      Profil
    </Anchor>
  );
  if (!rollup) {
    return (
      <>
        <div className="tl-detail-eyebrow tl-detail-eyebrow--split">
          IZMENE OSEBE {profile}
        </div>
        <Text size="xs" c="dimmed">
          razpored za druge dni se še nalaga
        </Text>
      </>
    );
  }
  const refusedN = answeredRefusals(volunteer, rollup).length;
  const openN = unansweredShifts(volunteer, rollup).length;
  // Nothing refused and nothing open is not yet agreement: somebody holding
  // only proposals or finished shifts has confirmed nothing.
  const agreedN = confirmedShifts(volunteer, rollup).length;
  return (
    <>
      <div className="tl-detail-eyebrow tl-detail-eyebrow--split">
        IZMENE OSEBE · {rollup.shifts.length} {profile}
      </div>
      <div className="tl-detail-shifts">
        {rollup.shifts.map((s) => {
          const current = s.id === shiftId;
          // Only an assigned shift is asked anything: a proposal was never on a
          // briefing, a finished shift has nothing left to answer.
          const chip =
            s.state === 'proposed'
              ? { text: 'predlog', fg: 'oklch(0.45 0.08 285)', bg: 'oklch(0.965 0.02 285)' }
              : answers && s.state === 'assigned'
                ? ANSWER_CHIP[answers[s.id] ?? 'none']
                : null;
          return (
            <button
              key={s.id}
              type="button"
              className={`tl-detail-shift${current ? ' tl-detail-shift--current' : ''}`}
              disabled={current}
              onClick={() =>
                // The same day keeps the board as it is — sector focus included;
                // another day is a trip there.
                s.eventDayId === dayId ? selectShift(s.id) : goShift(s.eventDayId, s.id)
              }
            >
              <span className="tl-detail-shift-day">{s.dayLabel.slice(0, 3)}</span>
              <span className="tl-detail-shift-time">{range(s.fromMin, s.toMin)}</span>
              <span className="tl-detail-shift-post">
                {s.postCode} · {s.postMicro}
              </span>
              {chip ? (
                <span className="tl-detail-chip" style={{ color: chip.fg, background: chip.bg }}>
                  {chip.text}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {answers ? (
        <Text size="xs" c="dimmed">
          Razpored v{volunteer.scheduleVersion}
          {refusedN > 0 ? ` · ${refusedN} ${plural(refusedN, 'zavrnjena', 'zavrnjeni', 'zavrnjene', 'zavrnjenih')}` : ''}
          {openN > 0 ? ` · ${openN} brez odgovora` : ''}
          {refusedN === 0 && openN === 0 && agreedN > 0 ? ' · vse izmene potrjene' : ''}
        </Text>
      ) : null}
    </>
  );
}

function ShiftDetail({
  post,
  shift,
  data,
  phase,
  live,
  now,
  planning,
}: {
  post: TimelinePost;
  shift: Shift;
  data: TimelinePayload;
  phase: DayPhase;
  live: boolean;
  now: number;
  planning: ShiftPlanning;
}) {
  const axis = useAxis();
  const { silentOverride, liveOverride, selectShift, selectGap, setAssignPreview } = useUi();
  const silentMode = silentOverride ?? (liveOverride ?? phase === 'live');
  const { update, remove } = useShiftMutations();
  const navigate = useNavigate();
  const { can } = useSession();
  // Not the same as `locked`, which says the shift is done (SPEC §1.1L).
  const readOnly = !can('plan:write');
  const volunteer = shift.volunteerId
    ? data.volunteers.find((v) => v.id === shift.volunteerId)
    : undefined;

  const [fromMin, setFromMin] = useState(String(shift.fromMin));
  const [toMin, setToMin] = useState(String(shift.toMin));
  const [meetMin, setMeetMin] = useState(shift.meetMin !== null ? String(shift.meetMin) : null);
  const [meetPlace, setMeetPlace] = useState(shift.meetPlace);
  const [note, setNote] = useState(shift.note);
  const [volunteerId, setVolunteerId] = useState(shift.volunteerId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Bumped to open the Oseba picker from "Poišči zamenjavo".
  const [pickerSignal, setPickerSignal] = useState(0);
  const [hoverId, setHoverId] = useState<string | null>(null);
  // Somebody we still brief: neither a team's place nor somebody not coming.
  const rosterPerson = volunteer !== undefined && !isExternal(volunteer) && !isCancelled(volunteer);
  // Recording an answer writes the person, not the shift.
  const actsOnPerson = rosterPerson && can('people:write');

  // What the form currently proposes, which is what the hints and the save bar
  // judge — not what is saved. Picking a person or moving Od/Do shows the
  // consequence before Shrani, rather than after it.
  const picked = volunteerId ? data.volunteers.find((v) => v.id === volunteerId) : undefined;
  const formRange = { fromMin: Number(fromMin), toMin: Number(toMin) };
  const reassigned = volunteerId !== shift.volunteerId;
  const retimed = formRange.fromMin !== shift.fromMin || formRange.toMin !== shift.toMin;
  // Everyone who could stand here over the form's times, by the autofill rules.
  const groups = useMemo(
    () => pieceCandidates(data, planning, { ...formRange, postId: post.id }, { holderId: shift.volunteerId ?? undefined }),
    // formRange is rebuilt every render; its two numbers are what matter.
    [data, planning, formRange.fromMin, formRange.toMin, post.id, shift.volunteerId],
  );
  const ranked = (id: string | null | undefined) =>
    id ? [...groups.rec, ...groups.warn, ...groups.flag, ...groups.busy].find((c) => c.person.id === id) : undefined;
  const pickedRank = reassigned ? ranked(volunteerId) : undefined;
  // The holder kept, but moved: only what they closed on the sheet is a reason
  // to hesitate — somebody not coming or a team has no sheet to overrule.
  const holderKept = !reassigned && picked && !isExternal(picked) && !isCancelled(picked) ? picked : undefined;
  const holderClosed = holderKept && retimed ? gapMismatch(holderKept, formRange, data.day.key) : [];
  const pickedChips = pickedRank ? reasonChips(pickedRank, data, planning, silentMode) : [];
  // Overruling somebody asks first, in the save bar: a newcomer against their
  // sheet, a refusal that day or the daily maximum, or the holder moved into
  // hours they closed. A note-only edit moves nobody and asks nothing.
  const problems =
    pickedRank?.group === 'busy'
      ? ['takrat že na drugem mestu']
      : pickedRank?.group === 'flag'
      ? pickedChips.filter((c) => c.tone === 'flag').map((c) => c.text)
      : holderClosed.length > 0
        ? [`v prijavnici »ni na voljo« za ${holderClosed.map(slotLabel).join(', ')}`]
        : [];
  const judged =
    pickedRank?.group === 'flag' || pickedRank?.group === 'busy' ? picked : holderClosed.length > 0 ? holderKept : undefined;
  // A trim answers a closed slot; it cannot move somebody out of another post.
  const trim = judged && pickedRank?.group !== 'busy' ? trimFor(judged.availability, data.day.key, formRange) : null;
  // Who a save puts back into confirmation: the displaced holder and the
  // newcomer, or the holder whose times moved — never under a silent edit.
  const briefed = (v: PoolVolunteer | undefined) =>
    v !== undefined && !isExternal(v) && !isCancelled(v) && !briefingStale(v);
  const reconfirm = silentMode
    ? []
    : reassigned
      ? [volunteer, picked].filter(briefed)
      : retimed && briefed(picked)
        ? [picked!]
        : [];

  // The board draws who would stand here: the person under the pointer in the
  // open picker, else the newcomer picked.
  const previewId = hoverId ?? (reassigned ? volunteerId : null);
  useEffect(() => {
    const person = previewId ? data.volunteers.find((v) => v.id === previewId) : undefined;
    const rank = ranked(previewId);
    setAssignPreview(
      person && previewId !== shift.volunteerId
        ? {
            postId: post.id,
            ...formRange,
            volunteerId: person.id,
            name: person.name,
            tone: isExternal(person)
              ? 'team'
              : rank?.group === 'flag' || rank?.group === 'busy'
                ? 'flag'
                : rank?.group === 'warn'
                  ? 'warn'
                  : 'rec',
          }
        : null,
    );
  }, [previewId, formRange.fromMin, formRange.toMin, groups]);
  useEffect(() => () => setAssignPreview(null), [setAssignPreview]);

  const dirty =
    Number(fromMin) !== shift.fromMin ||
    Number(toMin) !== shift.toMin ||
    (meetMin !== null ? Number(meetMin) : null) !== shift.meetMin ||
    meetPlace !== shift.meetPlace ||
    note !== shift.note ||
    volunteerId !== shift.volunteerId;

  function resetForm() {
    setFromMin(String(shift.fromMin));
    setToMin(String(shift.toMin));
    setMeetMin(shift.meetMin !== null ? String(shift.meetMin) : null);
    setMeetPlace(shift.meetPlace);
    setNote(shift.note);
    setVolunteerId(shift.volunteerId);
  }

  /**
   * Save the form over `range`. Everything but Od/Do comes from the form as it
   * stands; the range is a parameter because accepting a trim saves the shift
   * shorter than the form shows, and the hours given back become an uncovered
   * gap on a post that read as covered.
   */
  function saveWith(range: MinuteRange) {
    update.mutate({
      id: shift.id,
      updatedAt: shift.updatedAt,
      fromMin: range.fromMin,
      toMin: range.toMin,
      volunteerId,
      meetMin: meetMin !== null ? Number(meetMin) : null,
      meetPlace,
      note,
      silent: silentMode,
    });
  }

  /** Nobody stands here any more: the row turns to who could. */
  function free() {
    void update
      .mutateAsync({
        id: shift.id,
        updatedAt: shift.updatedAt,
        volunteerId: null,
        state: 'open',
        silent: silentMode,
      })
      .then(() => selectGap({ postId: post.id, fromMin: shift.fromMin, toMin: shift.toMin, shiftId: shift.id }))
      // The hook already says what went wrong.
      .catch(() => undefined);
  }

  const sectorName = data.sectors.find((s) => s.id === post.sectorId)?.name ?? '';
  // Od/Do stay inside the post's opening window, the same clamp the block's
  // drag handles obey. Zbor ob deliberately does not — a briefing is often
  // called before the post opens.
  const window = shiftWindow(post, shift, axis);
  const locked = shift.state === 'done';
  const canEdit = !locked && !readOnly;
  const team = volunteer && isExternal(volunteer)
    ? data.externalGroups.find((g) => g.id === volunteer.externalGroupId)
    : undefined;

  return (
    <Stack gap={6}>
      <Group gap={8} wrap="nowrap" align="flex-start">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={8} align="center">
            <PostLink postId={post.id}>
              <Text component="span" size="lg" fw={700} style={{ fontFamily: 'var(--rr-font-mono)' }}>
                {post.code}
              </Text>
            </PostLink>
            <span className="tl-detail-when">
              {data.day.label.slice(0, 3)} {range(shift.fromMin, shift.toMin)} ·{' '}
              {formatDuration(shift.toMin - shift.fromMin)}
            </span>
          </Group>
          <PostLink postId={post.id}>
            <Text component="span" size="sm">
              {post.micro}
            </Text>
          </PostLink>
          <Text size="xs" c="dimmed">
            {sectorName} · {post.type}
            {post.urgent ? ' · nujno mesto' : ''}
          </Text>
        </div>
        {canEdit ? (
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <Button size="compact-sm" variant="default" aria-label="Dejanja izmene">
                ⋯
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  update.mutate({
                    id: shift.id,
                    updatedAt: shift.updatedAt,
                    state: 'cancelled',
                    silent: silentMode,
                  })
                }
              >
                Odpovej izmeno
              </Menu.Item>
              <Menu.Item color="red" onClick={() => setConfirmDelete(true)}>
                Izbriši izmeno…
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </Group>

      {confirmDelete ? (
        <div className="tl-detail-confirm">
          <Text size="xs">
            <b>
              Izbrišem izmeno {post.code} {range(shift.fromMin, shift.toMin)}?
            </b>{' '}
            Tega ni mogoče razveljaviti.
          </Text>
          <Group gap={6}>
            <Button
              size="compact-sm"
              color="red"
              variant="light"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate({ id: shift.id, silent: silentMode });
                selectShift(null);
              }}
            >
              Izbriši
            </Button>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={() => setConfirmDelete(false)}>
              Prekliči
            </Button>
          </Group>
        </div>
      ) : null}

      {locked ? (
        <Text size="xs" c="dimmed">
          Izmena opravljena — zaklenjena.
        </Text>
      ) : null}

      <LiveCheckinCard shift={shift} data={data} phase={phase} live={live} now={now} />

      {volunteer ? (
        <>
          <div className="tl-detail-eyebrow">{isExternal(volunteer) ? 'ZUNANJA EKIPA' : 'OSEBA NA IZMENI'}</div>
          <div
            className={`tl-detail-person${
              rosterPerson &&
              shift.state === 'assigned' &&
              shiftConfirmation(shift, volunteer) === 'zavrnjen'
                ? ' tl-detail-person--refused'
                : ''
            }`}
          >
            <Group gap={8} wrap="nowrap" className="tl-detail-person-head">
              <div className={`tl-avatar${isExternal(volunteer) ? ' tl-avatar--team' : ''}`}>
                {isExternal(volunteer) ? '▪' : initials(volunteer.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {isExternal(volunteer) ? (
                  /*
                   * A team is outside the confirmation lifecycle (SPEC §1.1J): no
                   * answer to show or record. What is useful instead is who to phone.
                   */
                  <>
                    <Text size="sm" fw={600} truncate>
                      {holderLabel(volunteer)}
                    </Text>
                    {team ? (
                      <Text size="xs" c="dimmed">
                        {team.leaderName || team.name}
                        {team.leaderPhone ? ` · ${team.leaderPhone}` : ''}
                      </Text>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Anchor
                      component="button"
                      size="sm"
                      fw={600}
                      onClick={() => void navigate(`/prostovoljci?volunteer=${volunteer.id}`)}
                    >
                      {volunteer.name}
                    </Anchor>
                    {volunteer.phone ? (
                      <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--rr-font-mono)' }}>
                        <a className="tl-detail-tel" href={`tel:${volunteer.phone.replace(/\s+/g, '')}`}>
                          {volunteer.phone}
                        </a>
                      </Text>
                    ) : null}
                  </>
                )}
              </div>
              {canEdit ? (
                <Button size="compact-xs" variant="default" onClick={() => free()}>
                  Sprosti
                </Button>
              ) : null}
            </Group>

            {isCancelled(volunteer) ? (
              /*
               * Somebody who is not coming, on a shift they kept — past, or under
               * way with a check-in (SPEC §1.1K). No answer to ask for; what helps
               * here is why, and the way to their card.
               */
              <div className="tl-detail-answer">
                <Group gap={6}>
                  <span className="tl-status-dot tl-status-dot--nepride" />
                  <Text size="sm" fw={600} style={{ color: 'oklch(0.45 0.12 25)' }}>
                    ne pride
                  </Text>
                  <Text size="xs" c="dimmed">
                    zabeleženo {recordedOn(volunteer.cancelledAt)}
                  </Text>
                </Group>
                {volunteer.cancelReason ? (
                  <div className="tl-detail-quote">„{volunteer.cancelReason}“</div>
                ) : null}
              </div>
            ) : null}

            {/* This shift's own answer, where the payload carries answers (SPEC §1.1F) —
                for a shift the person holds; a proposal asks nobody yet. */}
            {rosterPerson && volunteer.shiftAnswers && shift.state === 'assigned' ? (
              <ShiftAnswerBox
                volunteerId={volunteer.id}
                shiftId={shift.id}
                answer={volunteer.shiftAnswers[shift.id]}
                canWrite={actsOnPerson}
                onReplace={canEdit ? () => setPickerSignal((n) => n + 1) : undefined}
              />
            ) : null}
          </div>

          {!isExternal(volunteer) ? (
            <PersonShifts volunteer={volunteer} shiftId={shift.id} dayId={data.day.id} />
          ) : null}
        </>
      ) : null}

      <div className="tl-detail-eyebrow">IZMENA</div>
      <Group gap={6} grow>
        <Select
          size="xs"
          label="Od"
          data={timeOptions(window, shift.fromMin)}
          value={fromMin}
          onChange={(v) => v && setFromMin(v)}
          searchable
          disabled={!canEdit}
        />
        <Select
          size="xs"
          label="Do"
          data={timeOptions(window, shift.toMin)}
          value={toMin}
          onChange={(v) => v && setToMin(v)}
          searchable
          disabled={!canEdit}
        />
      </Group>
      <PersonPicker
        data={data}
        planning={planning}
        silent={silentMode}
        groups={groups}
        formRange={formRange}
        holderId={shift.volunteerId}
        value={volunteerId}
        onChange={setVolunteerId}
        onHover={setHoverId}
        openSignal={pickerSignal}
        disabled={!canEdit}
      />
      {pickedRank && pickedRank.group !== 'flag' && pickedRank.group !== 'busy' ? (
        pickedChips.some((c) => c.tone === 'warn') ? (
          <Stack gap={2}>
            {pickedChips
              .filter((c) => c.tone === 'warn')
              .map((c) => (
                <FormHint key={c.text}>{c.text}</FormHint>
              ))}
          </Stack>
        ) : (
          <Text size="xs" style={{ color: 'oklch(0.42 0.08 245)' }}>
            ✓{' '}
            {pickedChips
              .filter((c) => c.tone === 'good' || c.tone === 'info')
              .map((c) => c.text)
              .join(', ') || 'brez opozoril'}{' '}
            · <Load beforeMin={pickedRank.beforeMin} afterMin={pickedRank.afterMin} planning={planning} />
          </Text>
        )
      ) : null}
      {problems.length > 0 ? (
        <Stack gap={2}>
          {problems.map((t) => (
            <FormHint key={t} tone="red">
              {t}
            </FormHint>
          ))}
        </Stack>
      ) : null}
      <Group gap={6} grow>
        <Select
          size="xs"
          label="Zbor ob"
          placeholder="—"
          data={timeOptions(axis, shift.meetMin)}
          value={meetMin}
          onChange={setMeetMin}
          searchable
          clearable
          disabled={!canEdit}
        />
        <TextInput
          size="xs"
          label="Zborno mesto"
          value={meetPlace}
          onChange={(e) => setMeetPlace(e.currentTarget.value)}
          disabled={!canEdit}
        />
      </Group>
      <Textarea
        size="xs"
        label="Zabeležka"
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
        autosize
        minRows={1}
        disabled={!canEdit}
      />

      {dirty && canEdit ? (
        problems.length > 0 ? (
          <div className="tl-detail-savebar tl-detail-savebar--flag">
            <Text size="xs" fw={700} style={{ flex: 1, color: 'oklch(0.45 0.12 25)' }}>
              {judged?.name}: {problems.join(' · ')}
            </Text>
            {trim ? (
              <Button size="compact-sm" loading={update.isPending} onClick={() => saveWith(trim)}>
                Shrani {range(trim.fromMin, trim.toMin)}
              </Button>
            ) : null}
            <Button
              size="compact-sm"
              variant="light"
              color="orange"
              loading={update.isPending}
              onClick={() => saveWith(formRange)}
            >
              Vseeno shrani
            </Button>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={resetForm}>
              Prekliči
            </Button>
          </div>
        ) : (
          <div className="tl-detail-savebar">
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Neshranjeno
              {reconfirm.length > 0
                ? ` · ✉ ${reconfirm.map((v) => v!.name.split(' ')[0]).join(', ')} znova potrjuje`
                : ''}
            </Text>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={resetForm}>
              Prekliči
            </Button>
            <Button size="compact-sm" loading={update.isPending} onClick={() => saveWith(formRange)}>
              Shrani {silentMode ? '(brez ponovne potrditve)' : ''}
            </Button>
          </div>
        )
      ) : null}
    </Stack>
  );
}

function FormHint({ children, tone }: { children: React.ReactNode; tone?: 'red' }) {
  return (
    <Group gap={6} wrap="nowrap" align="flex-start">
      <Text size="xs" style={{ color: tone === 'red' ? 'oklch(0.58 0.15 25)' : 'var(--rr-warn)', fontWeight: 700 }}>
        ▲
      </Text>
      <Text size="xs" style={{ color: tone === 'red' ? 'oklch(0.45 0.12 25)' : 'var(--rr-warn-strong)', minWidth: 0 }}>
        {children}
      </Text>
    </Group>
  );
}
